import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, localTimestamp } from '../api';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { getWordsByGroup, putProgress, getLearnableGroups } from '../db';
import { triggerAutoSync } from '../sync';
import './StudyMode.css';

function StudyMode() {
    const { groupId } = useParams();
    const navigate = useNavigate();
    const isOnline = useOnlineStatus();
    // 学习会话开始时间(毫秒时间戳), 用于计算本次学习的实际时长
    const sessionStartRef = useRef(Date.now());
    const [words, setWords] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [phase, setPhase] = useState('learning'); // learning, flashcard, correction
    const [userInput, setUserInput] = useState("");
    const [correctionInput, setCorrectionInput] = useState("");
    const [isSpeaking, setIsSpeaking] = useState(false);

    const hasSpokenRef = useRef(null);
    const transitioningRef = useRef(false);
    const submittedRef = useRef(false);  // 防止完成时重复提交进度
    const currentWord = words[currentIndex];

    // 朗读逻辑：通过后端 TTS 代理（有道美式发音）播放两遍，间隔 500ms
    // 朗读期间 isSpeaking=true，阻止进入闪记模式
    const speak = useCallback((text) => {
        if (!text) return;
        const url = api.ttsUrl(text);
        setIsSpeaking(true);
        const finish = () => setIsSpeaking(false);
        const audio1 = new Audio(url);
        audio1.play().catch(err => {
            console.error("TTS play failed:", err);
            finish();
        });
        audio1.onended = () => {
            setTimeout(() => {
                const audio2 = new Audio(url);
                audio2.play().catch(err => {
                    console.error("TTS play failed:", err);
                    finish();
                });
                audio2.onended = finish;
            }, 500);
        };
    }, []);

    // 离线时检查可学习范围 (规格 7: 仅未完成组 + 最后完成组后5组可离线学习)
    useEffect(() => {
        if (isOnline) return;  // 在线时所有组均可学
        getLearnableGroups().then(groups => {
            if (!groups.includes(groupId)) {
                alert('该组不可离线学习（仅未完成组与最后完成组后的5组可离线学习）');
                navigate('/');
            }
        });
    }, [isOnline, groupId, navigate]);

    // 获取单词数据 (在线走网络, 离线降级读 IndexedDB)
    useEffect(() => {
        api.getWords(groupId)
            .then(res => setWords(res.data))
            .catch(async () => {
                const local = await getWordsByGroup(groupId);
                setWords(local);
            });
    }, [groupId]);

    // 自动朗读逻辑
    useEffect(() => {
        if (currentWord && phase === 'learning') {
            if (hasSpokenRef.current !== currentWord.id) {
                speak(currentWord.word);
                hasSpokenRef.current = currentWord.id;
            }
        }
    }, [currentIndex, currentWord, phase, speak]);

    // 全局键盘监听：在学习模式下按回车进入闪记（朗读中禁用）
    useEffect(() => {
        const handleGlobalKeyDown = (event) => {
            if (transitioningRef.current) return;
            if (isSpeaking) return;
            if (phase === 'learning' && event.key === 'Enter') {
                setPhase('flashcard');
            }
        };
        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    }, [phase, isSpeaking]);

    const handleNext = async () => {
        transitioningRef.current = true;
        if (currentIndex < words.length - 1) {
            setCurrentIndex(currentIndex + 1);
            setPhase('learning');
            setUserInput("");
            setCorrectionInput("");
            setTimeout(() => { transitioningRef.current = false; }, 300);
        } else {
            if (submittedRef.current) return;   // 防重入(按住Enter等场景)
            submittedRef.current = true;
            const now = localTimestamp();
            // 本次学习实际时长(秒) = 完成时刻 - 会话开始时刻
            const durationSec = Math.max(0, Math.round((Date.now() - sessionStartRef.current) / 1000));
            // 本地优先: 先写本地 dirty (必定保存, 不丢记录)
            await putProgress(groupId, 'study', now, true, durationSec);
            // 在线则后台自动同步 (成功清 dirty, 失败保留)
            if (isOnline) triggerAutoSync();
            alert("恭喜完成本组学习！");
            navigate('/');
        }
    };

    const checkFlashcard = () => {
        if (userInput.toLowerCase().trim() === currentWord.word.toLowerCase().trim()) {
            handleNext();
        } else {
            setUserInput("");           // 清空闪记输入框，确保回到闪记时为空
            setPhase('correction');
            setCorrectionInput("");
            // 错误时读一遍正确发音提醒
            const audio = new Audio(api.ttsUrl(currentWord.word));
            audio.play().catch(err => console.error("TTS play failed:", err));
        }
    };

    // 抄写正确后回到闪记模式重试
    const retryFlashcard = () => {
        setUserInput("");
        setPhase('flashcard');
    };

    if (!currentWord) return <div className="loading">加载中...</div>;

    // 阻止复制/剪切/粘贴/右键，防止单词被复制后粘贴作弊
    const blockClipboard = (e) => e.preventDefault();

    // 只保留 ASCII 可见字符并转小写，过滤中文/全角/emoji
    // iPad Safari 横屏可能默认中文输入法联想，此过滤保证最终值始终为英文小写
    const sanitize = (s) => s.replace(/[^\x20-\x7E]/g, '').toLowerCase();

    // 输入框统一属性：强制英文输入、禁用中文输入法/自动大写/自动补全/粘贴
    const inputProps = {
        type: "text",
        lang: "en",
        inputMode: "latin",          // 唤起英文/拉丁字母软键盘
        autoComplete: "off",
        autoCorrect: "off",
        autoCapitalize: "off",       // 首字母不大写
        spellCheck: false,
        onPaste: blockClipboard,
        onContextMenu: blockClipboard,
        onCompositionStart: blockClipboard, // 阻止输入法联想
    };

    return (
        <div
            className="study-container"
            onCopy={blockClipboard}
            onCut={blockClipboard}
            onPaste={blockClipboard}
            onContextMenu={blockClipboard}
        >
            <div className="study-card">
                {/* 只有在非闪记模式下显示图片 */}
                {phase !== 'flashcard' && (
                    <div className="image-section">
                        {currentWord.picture_id && (
                            <img src={api.pictureUrl(currentWord.picture_id)} alt="mindmap" />
                        )}
                    </div>
                )}

                <div className="content-section">
                    <div className="progress-tag">Group {groupId}: {currentIndex + 1} / {words.length}</div>
                    
                    {phase === 'learning' ? (
                        <div className="info-area">
                            <h1 className="word-text">{currentWord.word}</h1>
                            <div className="meaning-row">
                                <span className="chinese">{currentWord.chinese_meaning}</span>
                                <span className="pos">({currentWord.part_of_speech})</span>
                            </div>
                            
                            <div className="details-anim">
                                {currentWord.word_root && <p className="root"><strong>词根:</strong> {currentWord.word_root}</p>}
                                {currentWord.affix && <p className="affix"><strong>组成:</strong> {currentWord.affix}</p>}
                                <button
                                    className="action-btn"
                                    onClick={() => setPhase('flashcard')}
                                    disabled={isSpeaking}
                                >
                                    {isSpeaking ? "朗读中，请稍候..." : "进入闪记模式 (Enter)"}
                                </button>
                            </div>
                        </div>
                    ) : phase === 'flashcard' ? (
                        <div className="flashcard-area">
                            <h2 className="flashcard-title">闪记模式：请拼写单词</h2>
                            <p className="flashcard-hint">中文含义：{currentWord.chinese_meaning}</p>
                            <input
                                autoFocus
                                value={userInput}
                                onChange={(e) => setUserInput(sanitize(e.target.value))}
                                onKeyDown={(e) => e.key === 'Enter' && checkFlashcard()}
                                placeholder="输入英文单词..."
                                {...inputProps}
                            />
                            <button className="action-btn" onClick={checkFlashcard}>检查 (Enter)</button>
                        </div>
                    ) : (
                        <div className="correction-area">
                            <h2 className="error-tip">回答错误！正确答案：{currentWord.word}</h2>
                            <p>请抄写一遍以解锁：</p>
                            <input
                                autoFocus
                                className={correctionInput.toLowerCase() === currentWord.word.toLowerCase() ? "correct" : "typing"}
                                value={correctionInput}
                                onChange={(e) => setCorrectionInput(sanitize(e.target.value))}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && correctionInput.toLowerCase() === currentWord.word.toLowerCase()) {
                                        retryFlashcard();
                                    }
                                }}
                                placeholder="请在此抄写..."
                                {...inputProps}
                            />
                            {correctionInput.toLowerCase() === currentWord.word.toLowerCase() && (
                                <button className="action-btn" onClick={retryFlashcard}>
                                    重新尝试 (Enter)
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <button className="back-home" onClick={() => navigate('/')}>返回首页</button>
        </div>
    );
}

export default StudyMode;
